import React, { useEffect } from 'react'
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Select,
    Link,
    InputLabel,
    Checkbox,
    TextField,
    Typography,
} from '@mui/material'
import { Form } from 'react-router-dom'

const numberOfNodesOptions = generateNumberOfNodes()

// Single source of truth for the text-processing language options (values,
// labels, and the disabled group dividers). Rendered by the Settings page
// below and reused by the onboarding guide's language picker — MUI requires
// MenuItem to be a direct Select child, so this is a function returning an
// array, not a component.
export function userLanguageMenuItems() {
    return [
        <MenuItem key="auto" value="auto">
            Auto-Detect (Default)
        </MenuItem>,
        <MenuItem key="zz" value="zz">
            Do not detect
        </MenuItem>,
        <MenuItem key="divider-popular" disabled>
            -- Most Popular: --
        </MenuItem>,
        <MenuItem key="en" value="en">
            English
        </MenuItem>,
        <MenuItem key="fr" value="fr">
            French
        </MenuItem>,
        <MenuItem key="de" value="de">
            German
        </MenuItem>,
        <MenuItem key="divider-full" disabled>
            -- Full Support: --
        </MenuItem>,
        <MenuItem key="nl" value="nl">
            Dutch
        </MenuItem>,
        <MenuItem key="it" value="it">
            Italian
        </MenuItem>,
        <MenuItem key="no" value="no">
            Norwegian
        </MenuItem>,
        <MenuItem key="ru" value="ru">
            Russian
        </MenuItem>,
        <MenuItem key="sp" value="sp">
            Spanish
        </MenuItem>,
        <MenuItem key="vp" value="vp">
            Swedish
        </MenuItem>,
        <MenuItem key="pt" value="pt">
            Portugese
        </MenuItem>,
        <MenuItem key="divider-beta" disabled>
            -- Beta Support: --
        </MenuItem>,
        <MenuItem key="ar" value="ar">
            Arabic
        </MenuItem>,
        <MenuItem key="cn" value="cn">
            Chinese Simplified
        </MenuItem>,
        <MenuItem key="tw" value="tw">
            Chinese Traditional
        </MenuItem>,
        <MenuItem key="he" value="he">
            Hebrew
        </MenuItem>,
        <MenuItem key="jp" value="jp">
            Japanese
        </MenuItem>,
        <MenuItem key="ko" value="ko">
            Korean
        </MenuItem>,
        <MenuItem key="fa" value="fa">
            Persian
        </MenuItem>,
        <MenuItem key="ep" value="ep">
            English (Porter Stemmer)
        </MenuItem>,
    ]
}

export function UserLanguageSelectMenu({ userLanguage, setUserLanguage }) {
    const [presetLanguage, setPresetLanguage] = React.useState(userLanguage)

    React.useEffect(() => {
        setPresetLanguage(userLanguage)
    }, [userLanguage])

    const handleSelectChange = event => {
        setUserLanguage(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="languageProcessing-label">
                Language Processing
            </InputLabel>
            <Select
                labelId="languageProcessing-label"
                value={presetLanguage}
                displayEmpty
                onChange={handleSelectChange}
                label="Language Processing"
            >
                {userLanguageMenuItems()}
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                If you tend to use only one language, we recommend to select it
                above for faster processing. To learn more about the text
                processing algorithm used in InfraNodus and for citations, see
                our{' '}
                <a
                    href="https://dl.acm.org/doi/10.1145/3308558.3314123"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ cursor: 'pointer' }}
                >
                    research paper
                </a>
                .
            </FormHelperText>
        </FormControl>
    )
}

export function UserPaletteSelectMenu({ userPalette, setUserPalette }) {
    const [presetPalette, setPresetPalette] = React.useState(userPalette)

    React.useEffect(() => {
        setPresetPalette(userPalette)
    }, [userPalette])

    const handleSelectChange = event => {
        setUserPalette(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="colorPalette-label">Graph Color Palette</InputLabel>
            <Select
                labelId="colorPalette-label"
                value={presetPalette}
                displayEmpty
                onChange={handleSelectChange}
                label="Graph Color Palette"
            >
                <MenuItem value="default">Standard Color (Default)</MenuItem>
                <MenuItem value="darker">Darker Color</MenuItem>
                <MenuItem value="color">Subdued Color</MenuItem>
                <MenuItem value="mono">Monochrome</MenuItem>
                <MenuItem value="contrast">Contrast Color</MenuItem>
            </Select>
            {/* <FormHelperText sx={{ mt: 2 }}>
                Default color works the best, but subdued and monochrome are
                also cool.
            </FormHelperText> */}
        </FormControl>
    )
}

export function UserBackgroundSelectMenu({
    userBackground,
    setUserBackground,
}) {
    const [presetBackground, setPresetBackground] = React.useState(
        userBackground
    )

    React.useEffect(() => {
        setPresetBackground(userBackground)
    }, [userBackground])

    const handleSelectChange = event => {
        setUserBackground(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="backgroundColor-label">
                Background Graph Color
            </InputLabel>
            <Select
                labelId="backgroundColor-label"
                value={presetBackground}
                displayEmpty
                onChange={handleSelectChange}
                label="Graph Background Color"
            >
                <MenuItem value="default">Light Background (Default)</MenuItem>
                <MenuItem value="dark">Dark Background</MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserNodeLabelsThresholdSelectMenu({
    userNodeLabelsThreshold,
    setUserNodeLabelsThreshold,
}) {
    const [
        presetNodeLabelsThreshold,
        setPresetNodeLabelsThreshold,
    ] = React.useState(userNodeLabelsThreshold)

    React.useEffect(() => {
        setPresetNodeLabelsThreshold(userNodeLabelsThreshold)
    }, [userNodeLabelsThreshold])

    const handleSelectChange = event => {
        setUserNodeLabelsThreshold(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="nodeLabelsThreshold-label">
                Node Labels Threshold
            </InputLabel>
            <Select
                labelId="nodeLabelsThreshold-label"
                value={presetNodeLabelsThreshold}
                displayEmpty
                onChange={handleSelectChange}
                label="Show Node Labels"
            >
                <MenuItem disabled>-- Recommended: --</MenuItem>
                <MenuItem value="8">For big nodes only (Default)</MenuItem>
                <MenuItem value="17">Do not show labels at all</MenuItem>
                <MenuItem value="0">For all the nodes</MenuItem>
                <MenuItem disabled>-- Expert Mode: --</MenuItem>

                <MenuItem value="12">
                    For bigger nodes only (from size 12)
                </MenuItem>
                <MenuItem value="7">For smaller nodes (from size 7)</MenuItem>
                <MenuItem value="6">For smaller nodes (from size 6)</MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserNodeLabelsSizeSelectMenu({
    userNodeLabelsSize,
    setUserNodeLabelsSize,
}) {
    const [presetNodeLabelsSize, setPresetNodeLabelsSize] = React.useState(
        userNodeLabelsSize
    )

    React.useEffect(() => {
        setPresetNodeLabelsSize(userNodeLabelsSize)
    }, [userNodeLabelsSize])

    const handleSelectChange = event => {
        setUserNodeLabelsSize(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="nodeLabelsSize-label">Node Labels Size</InputLabel>
            <Select
                labelId="nodeLabelsSize-label"
                value={presetNodeLabelsSize}
                displayEmpty
                onChange={handleSelectChange}
                label="Node Labels Size"
            >
                <MenuItem value="proportional">
                    Proportional to the Nodes' Size (Default)
                </MenuItem>
                <MenuItem value="fixed">Fixed Size</MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserEdgesTypeSelectMenu({ userEdgesType, setUserEdgesType }) {
    const [presetEdgesType, setPresetEdgesType] = React.useState(userEdgesType)

    React.useEffect(() => {
        setPresetEdgesType(userEdgesType)
    }, [userEdgesType])

    const handleSelectChange = event => {
        setUserEdgesType(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="nodeEdgesType-label">Graph Edges Type</InputLabel>
            <Select
                labelId="nodeEdgesType-label"
                value={presetEdgesType}
                displayEmpty
                onChange={handleSelectChange}
                label="Graph Edges Type"
            >
                <MenuItem value="line">Edges as Lines (Default)</MenuItem>
                <MenuItem value="curve">Edges as Curves</MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserAbstractViewSelectMenu({
    userAbstractView,
    setUserAbstractView,
}) {
    const [presetAbstractView, setPresetAbstractView] = React.useState(
        userAbstractView
    )

    React.useEffect(() => {
        setPresetAbstractView(userAbstractView)
    }, [userAbstractView])

    const handleSelectChange = event => {
        setUserAbstractView(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="abstractView-label">
                Abstract the Graph (show only edges, no nodes)
            </InputLabel>
            <Select
                labelId="abstractView-label"
                value={presetAbstractView}
                displayEmpty
                onChange={handleSelectChange}
                label="Abstract the Graph (show only edges, no nodes)"
            >
                <MenuItem value="no">No, Leave Standard (Default)</MenuItem>
                <MenuItem value="yes">Yes, Show Structure Only</MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserBigramsSelectMenu({ userBigrams, setUserBigrams }) {
    const [presetBigrams, setPresetBigrams] = React.useState(userBigrams)

    React.useEffect(() => {
        setPresetBigrams(userBigrams)
    }, [userBigrams])

    const handleSelectChange = event => {
        setUserBigrams(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="bigramsView-label">
                Graph View Shows Bigrams
            </InputLabel>
            <Select
                labelId="bigramsView-label"
                value={presetBigrams}
                displayEmpty
                onChange={handleSelectChange}
                label="Graph View Shows Bigrams"
            >
                <MenuItem value="1">
                    "near" connections (4-grams + bigrams) (Default)
                </MenuItem>
                <MenuItem value="0">"next to" connections (bigrams)</MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                4-grams "near" connections take more context into account and
                follow the landscape reading model that emulates how short-term
                memory works.
            </FormHelperText>
        </FormControl>
    )
}

export function UserPhrasesSelectMenu({ userPhrases, setUserPhrases }) {
    const [presetPhrases, setPresetPhrases] = React.useState(userPhrases)

    React.useEffect(() => {
        setPresetPhrases(userPhrases)
    }, [userPhrases])

    const handleSelectChange = event => {
        setUserPhrases(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="phrasesView-label">
                How to Show Underscore Phrases
            </InputLabel>
            <Select
                labelId="phrasesView-label"
                value={presetPhrases}
                displayEmpty
                onChange={handleSelectChange}
                label="How to Show Underscore Phrases"
            >
                <MenuItem value="0">
                    with a space separating the words (Default)
                </MenuItem>
                <MenuItem value="1">
                    with an underscore separating the words
                </MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserNumberOfNodesSelectMenu({
    userNumberOfNodes,
    setUserNumberOfNodes,
}) {
    const [presetNumberOfNodes, setPresetNumberOfNodes] = React.useState(
        userNumberOfNodes
    )

    React.useEffect(() => {
        setPresetNumberOfNodes(userNumberOfNodes)
    }, [userNumberOfNodes])

    const handleSelectChange = event => {
        setUserNumberOfNodes(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="phrasesView-label">
                Maximum Nodes Shown in Graph
            </InputLabel>
            <Select
                labelId="phrasesView-label"
                value={presetNumberOfNodes}
                displayEmpty
                onChange={handleSelectChange}
                label="Maximum Nodes Shown in Graph"
            >
                {numberOfNodesOptions.map(numberOfNodes => (
                    <MenuItem key={numberOfNodes} value={numberOfNodes}>
                        {numberOfNodes}
                    </MenuItem>
                ))}
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                We recommend to use 150 nodes as more nodes on the graph will
                make it slower and too dense, while fewer nodes may omit
                important details.
            </FormHelperText>
        </FormControl>
    )
}

export function UserInfluentialNodesSelectMenu({
    userInfluentialNodes,
    setUserInfluentialNodes,
}) {
    const [presetInfluentialNodes, setPresetInfluentialNodes] = React.useState(
        userInfluentialNodes
    )

    React.useEffect(() => {
        setPresetInfluentialNodes(userInfluentialNodes)
    }, [userInfluentialNodes])

    const handleSelectChange = event => {
        setUserInfluentialNodes(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="phrasesView-label">
                Most Influential Nodes
            </InputLabel>
            <Select
                labelId="phrasesView-label"
                value={presetInfluentialNodes}
                displayEmpty
                onChange={handleSelectChange}
                label="Most Influential Nodes"
            >
                <MenuItem value="bc">
                    Highest Betweeness Centrality (Default)
                </MenuItem>
                <MenuItem value="degree">
                    Highest Degree (number of connections)
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                We use graph betweenness centrality to identify the most
                prominent topics as per{' '}
                <a
                    href="https://support.noduslabs.com/hc/en-us/articles/360013525700-Betweenness-Centrality-Topical-Brokers"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ cursor: 'pointer' }}
                >
                    this algorithm
                </a>
                . It emphasizes the words that connect different topical
                clusters (contexts) of a text together. However, you can also
                choose to use the node degree (the number of connections), which
                is closer to term's frequency.
            </FormHelperText>
        </FormControl>
    )
}

export function UserHashtagsSelectMenu({ userHashtags, setUserHashtags }) {
    const [presetHashtags, setPresetHashtags] = React.useState(userHashtags)

    React.useEffect(() => {
        setPresetHashtags(userHashtags)
    }, [userHashtags])

    const handleSelectChange = event => {
        setUserHashtags(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="hashtagsView-label">
                Visualize in the Graph
            </InputLabel>
            <Select
                labelId="hashtagsView-label"
                value={presetHashtags}
                displayEmpty
                onChange={handleSelectChange}
                label="Visualize in the Graph"
            >
                <MenuItem value="0">
                    prioritize #hashtags or [[wiki links]], if none - words
                </MenuItem>
                <MenuItem value="1">
                    everything: #hashtags, [[wiki links]], and words
                </MenuItem>
                <MenuItem value="2">
                    only #hashtags and [[wiki links]], ignore words
                </MenuItem>
                <MenuItem value="verbs">
                    only connections between the verbs (english)
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                If you're having problems displaying the data in the graph,
                choose the option to show everything: #hashtags, [[wiki links]],
                and words.
            </FormHelperText>
        </FormControl>
    )
}

export function UserHashtagMorphemesSelectMenu({
    userHashtagMorphemes,
    setUserHashtagMorphemes,
}) {
    const [presetHashtagMorphemes, setPresetHashtagMorphemes] = React.useState(
        userHashtagMorphemes
    )

    React.useEffect(() => {
        setPresetHashtagMorphemes(userHashtagMorphemes)
    }, [userHashtagMorphemes])

    const handleSelectChange = event => {
        setUserHashtagMorphemes(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="hashtagsView-label">#Hashtag Processing</InputLabel>
            <Select
                labelId="hashtagsView-label"
                value={presetHashtagMorphemes}
                displayEmpty
                onChange={handleSelectChange}
                label="#Hashtag Processing"
            >
                <MenuItem value="0">hashtags will stay as they are</MenuItem>
                <MenuItem value="1">
                    hashtags will be lemmatized (#bits &#8594; #bit)
                </MenuItem>
            </Select>
        </FormControl>
    )
}

export function UserSquarebracketsSelectMenu({
    userSquarebrackets,
    setUserSquarebrackets,
}) {
    const [presetSquarebrackets, setPresetSquarebrackets] = React.useState(
        userSquarebrackets
    )

    React.useEffect(() => {
        setPresetSquarebrackets(userSquarebrackets)
    }, [userSquarebrackets])

    const handleSelectChange = event => {
        setUserSquarebrackets(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="hashtagsView-label">
                Words Inside [Squarebrackets] Will Be
            </InputLabel>
            <Select
                labelId="hashtagsView-label"
                value={presetSquarebrackets}
                displayEmpty
                onChange={handleSelectChange}
                label="Words Inside [Squarebrackets] Will Be"
            >
                <MenuItem disabled>
                    -- Treated as Tags / Filter Caregories: --
                </MenuItem>
                <MenuItem value="0">
                    treated as tags, not added to the graph (Default)
                </MenuItem>
                <MenuItem value="remove">
                    treated as tags, not in the graph, removed from statements
                </MenuItem>
                <MenuItem value="entities">
                    treated as tags, added into the graph
                </MenuItem>
                <MenuItem disabled>
                    -- Treated as Standard Concepts, Not as Tags: --
                </MenuItem>
                <MenuItem value="standard">
                    processed as separate words, added into the graph
                </MenuItem>
                <MenuItem value="mentions">
                    treated as @mentions (connecting to words in the statement)
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                You can use the [square brackets] syntax to tag your statements
                and filter the graph later using these tags.
            </FormHelperText>
        </FormControl>
    )
}

export function UserWikilinksSelectMenu({ userWikilinks, setUserWikilinks }) {
    const [presetWikilinks, setPresetWikilinks] = React.useState(userWikilinks)

    React.useEffect(() => {
        setPresetWikilinks(userWikilinks)
    }, [userWikilinks])

    const handleSelectChange = event => {
        setUserWikilinks(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="hashtagsView-label">
                Words Inside [[Wiki Links]] Will Be
            </InputLabel>
            <Select
                labelId="hashtagsView-label"
                value={presetWikilinks}
                displayEmpty
                onChange={handleSelectChange}
                label="Words Inside [[Wiki Links]] Will Be"
            >
                <MenuItem value="entities">
                    saved into the graph as they are (Default)
                </MenuItem>
                <MenuItem value="only">
                    the only ones saved into graph, skipping all other words
                </MenuItem>
                <MenuItem value="standard">
                    treated like standard words, broken into concepts
                </MenuItem>
                <MenuItem value="mentions">
                    treated like mentions, connecting to words in the statement
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                You can use the [[wiki links]] to add phrases and named entities
                into your graphs. You can also choose to have them shown as a
                @mention in the graph to see how they link to related concepts.
            </FormHelperText>
        </FormControl>
    )
}

export function UserMentionsSelectMenu({ userMentions, setUserMentions }) {
    const [presetMentions, setPresetMentions] = React.useState(userMentions)

    React.useEffect(() => {
        setPresetMentions(userMentions)
    }, [userMentions])

    const handleSelectChange = event => {
        setUserMentions(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="mentionsView-label">@Mentions Will Be</InputLabel>
            <Select
                labelId="mentionsView-label"
                value={presetMentions}
                displayEmpty
                onChange={handleSelectChange}
                label="@Mentions Will Be"
            >
                <MenuItem value="0">
                    left as they are, connected to all nodes in a statement
                    (Default)
                </MenuItem>
                <MenuItem value="remove">
                    treated as words, the @ sign will be removed
                </MenuItem>
                <MenuItem value="link">
                    the @ sign is removed, each word connected to @mention but
                    not to each other
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                The @mentions syntax allows you to have two different types of
                nodes in the graph that are connected to all the nodes in the
                statement rather than only to the ones nearby.
            </FormHelperText>
        </FormControl>
    )
}

export function UserStopwordsMenu({
    userStopwords,
    setUserStopwords,
    userStopwordsOverride,
    setUserStopwordsOverride,
}) {
    const handleStopwordsChange = event => {
        setUserStopwords(event.target.value)
    }

    const handleStopwordsOverrideChange = event => {
        setUserStopwordsOverride(event.target.value)
    }

    return (
        <>
            <FormControl
                fullWidth
                sx={{
                    marginTop: 4,
                    marginBottom: 2,
                }}
            >
                <>
                    <Typography component="span" variant="subtitle1">
                        By default, InfraNodus deletes a predetermined set of
                        words, such as "the", "is", "a" from the graph. However,
                        you can add additional words to the permanent stopwords
                        list below, so that they are not shown in the graph.
                        Please, separate them with a comma, e.g.: done, idea,
                        problem
                    </Typography>
                    <TextField
                        id="stopwords"
                        label="Custom Stopwords List"
                        multiline
                        rows={4}
                        value={userStopwords}
                        onChange={handleStopwordsChange}
                        margin="normal"
                    />
                    <FormHelperText sx={{ mt: 2, mb: 4 }}>
                        If you don't want some words to be deleted, simply add
                        them above with a minus sign, separated by a comma.
                    </FormHelperText>
                </>
            </FormControl>
            <FormControl
                fullWidth
                sx={{
                    marginTop: 0,
                    marginBottom: 2,
                }}
            >
                <>
                    <InputLabel id="stopwordsOverride-label">
                        Stopwords List Above Will Be
                    </InputLabel>

                    <Select
                        labelId="stopwordsOverride-label"
                        id="stopwordsOverride"
                        value={userStopwordsOverride}
                        displayEmpty
                        onChange={handleStopwordsOverrideChange}
                        label="Stopwords List Above Will Be"
                    >
                        <MenuItem value="0">
                            used in combination with the default stopwords list
                        </MenuItem>
                        <MenuItem value="1">
                            used to override the default stopwords list
                        </MenuItem>
                    </Select>
                    <FormHelperText sx={{ mt: 2, mb: 4 }}>
                        You can choose to use only your custom stopwords list
                        above and override our default stopwords. Useful for
                        unsupported languages or if you'd like to have more
                        control as to which words are deleted.
                    </FormHelperText>
                </>
            </FormControl>
        </>
    )
}

export function UserSynonymsMenu({ userSynonyms, setUserSynonyms }) {
    const presetSynonyms = userSynonyms
        ? userSynonyms.split(';').join('\r')
        : ''

    const handleSynonymsChange = event => {
        setUserSynonyms(event.target.value)
    }

    return (
        <>
            <FormControl
                fullWidth
                sx={{
                    marginTop: 4,
                    marginBottom: 2,
                }}
            >
                <>
                    <Typography component="span" variant="subtitle1">
                        You can create your own list of synonyms, so that
                        certain words are replaced with them in your graphs.
                    </Typography>
                    <TextField
                        id="synonyms"
                        label="Custom Synonyms List"
                        multiline
                        rows={4}
                        value={presetSynonyms}
                        onChange={handleSynonymsChange}
                        margin="normal"
                    />
                    <FormHelperText sx={{ mt: 2, mb: 4 }}>
                        Please, list each synonym relation on a separate line,
                        separated by a colon. E.g:
                        <br />
                        machine:machine learning
                        <br />
                        learning:machine learning
                    </FormHelperText>
                </>
            </FormControl>
        </>
    )
}

// Retired with the legacy speech mode; the new voice input submits on
// every pause. Kept only so the DB field can be cleaned up separately.
export function UserVoiceSelectMenu({ userVoicePhrase, setUserVoicePhrase }) {
    const [presetVoicePhrase, setPresetVoicePhrase] = React.useState(
        userVoicePhrase
    )

    React.useEffect(() => {
        setPresetVoicePhrase(userVoicePhrase)
    }, [userVoicePhrase])

    const handleSelectChange = event => {
        setUserVoicePhrase(event.target.value)
    }

    return (
        <FormControl
            fullWidth
            sx={{
                marginTop: 4,
                marginBottom: 2,
            }}
        >
            <InputLabel id="voicePhrase-label">
                Submit Your Voice to Graph
            </InputLabel>
            <Select
                labelId="voicePhrase-label"
                value={presetVoicePhrase}
                displayEmpty
                onChange={handleSelectChange}
                label="Submit Your Voice to Graph"
            >
                <MenuItem value="00">
                    automatically on speech pause (Default)
                </MenuItem>
                <MenuItem value="01">when you click "Save" </MenuItem>
                <MenuItem value="visualise">
                    every time you say "visualize"
                </MenuItem>
                <MenuItem value="визуализировать">
                    every time you say "визуализировать"
                </MenuItem>
            </Select>
            <FormHelperText sx={{ mt: 2 }}>
                To turn on this feature, use Google Chrome and click the
                microphone button next to the text editor field.
            </FormHelperText>
        </FormControl>
    )
}

export function UserBrandingMenu({ userBrandingLabel, setUserBrandingLabel }) {
    const handleBrandingChange = event => {
        setUserBrandingLabel(event.target.value)
    }

    return (
        <>
            <FormControl
                fullWidth
                sx={{
                    marginTop: 4,
                    marginBottom: 2,
                }}
            >
                <>
                    <TextField
                        id="customBrandingLabel"
                        label="Custom Branding Label"
                        value={userBrandingLabel}
                        onChange={handleBrandingChange}
                        margin="normal"
                        inputProps={{
                            maxLength: 12,
                        }}
                    />
                    <FormHelperText sx={{ mt: 2, mb: 4 }}>
                        Customize the graph branding for sharing your content.
                        As the space is limited, please, use no more than 12
                        characters. Available to{' '}
                        <a href="/subscription">premium</a> subscribers only.
                    </FormHelperText>
                </>
            </FormControl>
        </>
    )
}

export function UserMidiMenu({ userMidi, setUserMidi }) {
    const [presetUserMidi, setPresetUserMidi] = React.useState(userMidi)

    const handleMidiChange = event => {
        setUserMidi({ ...userMidi, [event.target.name]: event.target.value })
    }

    React.useEffect(() => {
        setPresetUserMidi(userMidi)
    }, [userMidi])

    return (
        <>
            <FormControl
                fullWidth
                sx={{
                    marginTop: 4,
                    marginBottom: 2,
                }}
            >
                <InputLabel id="midiactive-label">MIDI Status</InputLabel>
                <Select
                    labelId="midiactive-label"
                    id="midiactive"
                    name="midiactive"
                    value={
                        presetUserMidi.midiactive
                            ? presetUserMidi.midiactive
                            : 'off'
                    }
                    onChange={handleMidiChange}
                    label="MIDI Status"
                >
                    <MenuItem value="off">MIDI Deactivated</MenuItem>
                    <MenuItem value="on">MIDI Activated</MenuItem>
                </Select>
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    You can send and receive MIDI signals using your USB
                    interface, so you can build graphs of music. Every node is a
                    note, every co-occurrence is the connection. The encoding
                    used (to send and to receive) is: the note (A-G), octave
                    (0-7), channel (1-7).
                </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <InputLabel id="mididevice-label">
                    MIDI Output Device ID
                </InputLabel>
                <Select
                    labelId="mididevice-label"
                    id="mididevice"
                    name="mididevice"
                    value={
                        presetUserMidi.mididevice
                            ? presetUserMidi.mididevice
                            : '00'
                    }
                    onChange={handleMidiChange}
                    label="MIDI Output Device ID"
                >
                    {renderSelectOptions(0, 3)}
                </Select>
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    Which device ID should we be using for MIDI output? Usually,
                    it's 00 or 01. You can check it in the browser's console.
                </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <InputLabel id="midiinput-label">
                    MIDI Input Device ID
                </InputLabel>
                <Select
                    labelId="midiinput-label"
                    id="midiinput"
                    name="midiinput"
                    value={
                        presetUserMidi.midiinput
                            ? presetUserMidi.midiinput
                            : 'no'
                    }
                    onChange={handleMidiChange}
                    label="MIDI Output Device ID"
                >
                    <MenuItem key="no" value="no">
                        Do Not Use
                    </MenuItem>
                    {renderSelectOptions(0, 3)}
                </Select>
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    Which device ID should we be using for MIDI input? Usually,
                    it's 00 or 01. You can check it in the browser's console.
                </FormHelperText>
            </FormControl>

            <FormControl
                fullWidth
                sx={{
                    marginTop: 4,
                    marginBottom: 2,
                }}
            >
                <InputLabel id="midinodechannel-label">
                    MIDI Output Channel for Nodes
                </InputLabel>
                <Select
                    labelId="midinodechannel-label"
                    id="midinodechannel"
                    name="midinodechannel"
                    value={
                        presetUserMidi.midinodechannel
                            ? presetUserMidi.midinodechannel
                            : '01'
                    }
                    onChange={handleMidiChange}
                    label="MIDI Output Channel for Nodes"
                >
                    {renderSelectOptions(1, 16)}
                </Select>

                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    When you add the new nodes or click on them, which MIDI
                    channel should the signal be sent to?
                </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <InputLabel id="midiedgechannel-label">
                    MIDI Output Channel for Edges
                </InputLabel>
                <Select
                    labelId="midiedgechannel-label"
                    id="midiedgechannel"
                    name="midiedgechannel"
                    value={
                        presetUserMidi.midiedgechannel
                            ? presetUserMidi.midiedgechannel
                            : '02'
                    }
                    onChange={handleMidiChange}
                    label="MIDI Output Channel for Edges"
                >
                    {renderSelectOptions(1, 16)}
                </Select>
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    When you add the new edges (connections between the nodes),
                    which MIDI channel should the signal be sent to?
                </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <InputLabel id="mididuration-label">
                    Duration of Output Note
                </InputLabel>
                <Select
                    labelId="mididuration-label"
                    id="mididuration"
                    name="mididuration"
                    value={
                        presetUserMidi.mididuration
                            ? presetUserMidi.mididuration
                            : '4000'
                    }
                    onChange={handleMidiChange}
                    label="Duration of Output"
                >
                    {[
                        '4000 ms',
                        '3000 ms',
                        '2000 ms',
                        '1000 ms',
                        '9000 ms',
                    ].map((duration, index) => (
                        <MenuItem
                            value={duration.replace(' ms', '')}
                            key={index}
                        >
                            {duration}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    How many milliseconds should the note be played for?
                </FormHelperText>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <TextField
                    label="MIDI Nodes Play Note"
                    id="midinodenote"
                    name="midinodenote"
                    value={presetUserMidi.midinodenote || 'C2'}
                    onChange={handleMidiChange}
                    margin="normal"
                    inputProps={{ maxLength: 2 }}
                />
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    What note should we play when you add / click a node? Use
                    the standard convention: C2, D1, E3, etc.
                </FormHelperText>
            </FormControl>
            <FormControl fullWidth margin="normal">
                <TextField
                    label="MIDI Edges Play Note"
                    id="midiedgenote"
                    name="midiedgenote"
                    value={presetUserMidi.midiedgenote || 'C2'}
                    onChange={handleMidiChange}
                    margin="normal"
                    inputProps={{ maxLength: 2 }}
                />
                <FormHelperText sx={{ mt: 2, mb: 4 }}>
                    What note should we play when you add / click a node? Use
                    the standard convention: C2, D1, E3, etc.
                </FormHelperText>
            </FormControl>
        </>
    )
}

function generateNumberOfNodes() {
    const options = []

    // From 10 to 150 with a step of 10
    for (let i = 10; i <= 150; i += 10) {
        options.push(i)
    }

    // From 200 to 500 with a step of 50
    for (let i = 200; i <= 500; i += 50) {
        options.push(i)
    }
    return options
}

const renderSelectOptions = (start, end) => {
    let options = []
    for (let i = start; i <= end; i++) {
        let value = i.toString().padStart(2, '0')
        options.push(
            <MenuItem value={value} key={value}>
                {value}
            </MenuItem>
        )
    }

    return options
}
